import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Download, Search, Trash2, Mail, BellRing, Loader2 } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { NewsletterComposer } from "./NewsletterComposer";
import { LikeAlertsList } from "./LikeAlertsList";
import { CopyEmails, FooterNewsletterToggle } from "./SubscriberTools";
import { WelcomeEmailSettings } from "./WelcomeEmailSettings";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const Stat = ({ id, label, value, Icon }) => (
  <div className="bg-white rounded-2xl border border-slate-200/80 p-4 flex items-center gap-3" data-testid={id}>
    <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center"><Icon size={18} className="text-[#F5551E]" /></span>
    <div><p className="text-2xl font-display font-bold text-[#12234A]">{value}</p><p className="text-xs text-slate-500">{label}</p></div>
  </div>
);

const Tag = ({ on, children }) => <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${on ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400 line-through"}`}>{children}</span>;

const SubRow = ({ s, onDelete }) => (
  <tr className="border-b border-slate-100 text-sm" data-testid={`sub-row-${s.email}`}>
    <td className="py-2.5 pr-3 font-medium text-[#12234A]">{s.email}{s.unsubscribed && <span className="ml-2 text-xs text-slate-400">(unsubscribed)</span>}</td>
    <td className="py-2.5 pr-3"><div className="flex gap-1 flex-wrap"><Tag on={s.newsletter}>Newsletter</Tag><Tag on={s.alerts}>Alerts</Tag></div></td>
    <td className="py-2.5 pr-3 text-slate-500">{s.alerts ? `${s.city}, ${s.province} · ${s.radius_km ? `${s.radius_km} km` : "province"}` : "—"}</td>
    <td className="py-2.5 pr-3 text-slate-500 uppercase text-xs">{s.lang}</td>
    <td className="py-2.5 pr-3 text-slate-500">{s.created_at?.slice(0, 10)}</td>
    <td className="py-2.5 text-right"><button onClick={() => onDelete(s)} data-testid={`sub-delete-${s.email}`} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 size={15} /></button></td>
  </tr>
);

const FILTERS = [
  { id: "all", label: "All", test: () => true, count: (c) => c.total + c.unsubscribed },
  { id: "newsletter", label: "Newsletter", test: (s) => s.newsletter && !s.unsubscribed, count: (c) => c.newsletter },
  { id: "alerts", label: "Alerts", test: (s) => s.alerts && !s.unsubscribed, count: (c) => c.alerts },
  { id: "unsubscribed", label: "Unsubscribed", test: (s) => s.unsubscribed, count: (c) => c.unsubscribed },
];

export function SubscribersTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [pending, setPending] = useState(null);
  const [dl, setDl] = useState(false);
  const load = () => api.get("/admin/subscribers").then((r) => setData(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);
  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.id === type).test;
    return (data?.items || []).filter((s) => f(s) && (!q || `${s.email} ${s.city}`.toLowerCase().includes(q.toLowerCase())));
  }, [data, q, type]);

  const download = async () => {
    setDl(true);
    try {
      const r = await api.get(`/admin/subscribers.csv?type=${type}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `critterposts-subscribers-${type === "all" ? "" : `${type}-`}${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errText(e)); }
    finally { setDl(false); }
  };
  const remove = async () => {
    const s = pending;
    setPending(null);
    try { await api.delete(`/admin/subscribers/${s.id}`); toast.success(`Removed ${s.email}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  if (!data) return <Loader2 className="animate-spin text-slate-400" />;
  const c = data.counts;
  return (
    <div className="space-y-6" data-testid="subscribers-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat id="sub-stat-total" label="Active subscribers" value={c.total} Icon={Users} />
        <Stat id="sub-stat-newsletter" label="Newsletter" value={c.newsletter} Icon={Mail} />
        <Stat id="sub-stat-alerts" label="Lost-pet alerts" value={c.alerts} Icon={BellRing} />
        <Stat id="sub-stat-unsub" label="Unsubscribed" value={c.unsubscribed} Icon={Trash2} />
      </div>
      <FooterNewsletterToggle />
      <WelcomeEmailSettings />
      <NewsletterComposer count={c.newsletter} />
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-display font-bold text-[#12234A]">Subscribers</h3>
          <div className="flex gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or city" className="h-10 pl-9 rounded-xl w-60" data-testid="sub-search" />
            </div>
            <CopyEmails rows={shown} label={{ all: "", newsletter: "newsletter", alerts: "lost-pet alert", unsubscribed: "unsubscribed" }[type]} />
            <Button variant="outline" onClick={download} disabled={dl || !shown.length} className="rounded-xl gap-1.5" data-testid="sub-download">
              {dl ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download CSV
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="sub-filters">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" onClick={() => setType(f.id)} data-testid={`sub-filter-${f.id}`} aria-pressed={type === f.id}
              className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${type === f.id ? "bg-[#12234A] border-[#12234A] text-white" : "bg-white border-slate-200 text-[#12234A] hover:border-[#F5551E]/50"}`}>
              {f.label} <span className={`ml-1 text-xs ${type === f.id ? "text-white/70" : "text-slate-400"}`}>{f.count(c)}</span>
            </button>
          ))}
        </div>
        {shown.length ? (
          <div className="overflow-x-auto max-h-[520px]">
            <table className="w-full" data-testid="sub-table">
              <thead><tr className="text-left text-xs text-slate-400 uppercase tracking-wide"><th className="pb-2">Email</th><th className="pb-2">Signed up for</th><th className="pb-2">Alert area</th><th className="pb-2">Lang</th><th className="pb-2">Joined</th><th /></tr></thead>
              <tbody>{shown.map((s) => <SubRow key={s.id} s={s} onDelete={setPending} />)}</tbody>
            </table>
          </div>
        ) : <p className="text-sm text-slate-400 py-8 text-center" data-testid="sub-empty">{data.items.length ? (type === "all" ? "No matches" : `No ${FILTERS.find((f) => f.id === type).label.toLowerCase()} subscribers${q ? " match" : " yet"}`) : "No sign-ups yet. Turn on “Collect emails” in Admin → Homepage → Popups."}</p>}
      </div>
      <LikeAlertsList />
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="sub-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pending?.email}?</AlertDialogTitle>
            <AlertDialogDescription>They'll stop getting the newsletter and lost-pet alerts.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sub-delete-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="sub-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
